// Locale detection + homepage content overrides for de-CH, fr-CH and ar.
// Tours/destinations data stays in English; static marketing copy is localized.

export const SUPPORTED_LOCALES = ["en", "de", "fr", "ar"];

export const RTL_LOCALES = ["ar"];

export const isRtlLocale = (locale) => RTL_LOCALES.includes(locale);

export const getLocaleFromPath = (pathname) => {
  const first = pathname.split("/")[1];
  return SUPPORTED_LOCALES.includes(first) && first !== "en" ? first : "en";
};

export const localePrefix = (locale) => (locale === "en" ? "" : `/${locale}`);

// Localized homepage content — merged over the site content for /de and /fr.
export const HOME_TRANSLATIONS = {
  de: {
    hero_badge: "Private & Luxus-Touren in der Schweiz",
    hero_line1: "Touren in der Schweiz",
    hero_line2: "ganz für Sie",
    hero_subtext:
      "Die Schweiz, ganz privat für Sie — majestätische Alpen, kristallklare Seen und märchenhafte Dörfer mit erfahrenen lokalen Guides. 20 Jahre Erfahrung in unvergesslichen Reisen, massgeschneidert für Sie.",
    hero_image_alt: "Schweizer Alpenpanorama auf einer Tour mit Voyage Switzerland",
    hero_btn_primary: "Tour buchen",
    hero_btn_secondary: "Touren entdecken",
    tours_eyebrow: "Unsere Touren",
    tours_title: "Touren in der Schweiz",
    tours_subtitle:
      "Handverlesene Reiserouten durch die Schweiz — geführt, malerisch und mühelos. Jede Tour mit lokalem Experten und flexibler Buchung.",
    services_eyebrow: "Unsere Dienstleistungen",
    services_title: "Alles für Ihre Schweiz-Reise",
    services_subtitle:
      "Von privaten Alpenabenteuern bis zur nahtlosen Ankunft am Flughafen — ein Team für Ihre gesamte Reise.",
    dest_eyebrow: "Destinationen",
    dest_title: "Wohin geht die Reise?",
    dest_subtitle:
      "Sechs Ikonen der Schweiz — wählen Sie ein Reiseziel, wir planen das perfekte Erlebnis.",
    why_eyebrow: "Der Voyage-Standard",
    why_title: "Warum Voyage Switzerland?",
    contact_eyebrow: "Kontakt",
    contact_title: "Planen wir Ihre Reise",
    contact_subtitle:
      "Erzählen Sie uns von Ihren Reiseträumen — wir antworten persönlich, in der Regel innerhalb von 24 Stunden.",
    footer_tagline:
      "Private geführte Touren durch die Schweiz. Unvergessliche Reiseziele mit lokalen Experten — schnell, einfach und zuverlässig.",
    editorial_eyebrow: "Der Voyage Guide",
    editorial_title: "Touren in der Schweiz, geplant von Menschen, die hier leben",
    editorial_p1:
      "Die Schweiz belohnt Reisende, die sich Zeit nehmen. Unsere privaten Touren in der Schweiz sind genau darauf aufgebaut: ein lokaler Fahrer-Guide am Steuer, eine Route nach Ihren Interessen und die Freiheit, dort zu verweilen, wo das Licht am schönsten ist. Ob Halbtagesausflug in ein Seedorf oder Ganztagesreise über Alpenpässe — jede Tour beinhaltet Abholung von Tür zu Tür, flexible Zeiten und ehrliche lokale Tipps, die in keinem Reiseführer stehen.",
    editorial_p2:
      "Von Zürich, Genf und Luzern bis Interlaken, Zermatt und St. Moritz — unsere Luxustouren decken die Ikonen des Landes und seine stillen Ecken gleichermassen ab. Paare, Familien und Gruppen bis zu 200 Personen reisen mit uns für Flughafentransfers, Sightseeing-Tage und mehrtägige Pakete — zwanzig Jahre Schweizer Gastfreundschaft, in Minuten gebucht und persönlich von unserem Team bestätigt.",
    editorial_p3:
      "Sie wissen nicht, wo Sie anfangen sollen? Nennen Sie uns Ihre Daten und Wünsche, und wir gestalten den Tag für Sie — unverbindlich, ohne versteckte Gebühren und mit Antwort innerhalb von 24 Stunden. Das ist der Standard, den wir seit unserer ersten Tour vor zwei Jahrzehnten halten.",
  },
  fr: {
    hero_badge: "Circuits privés & de luxe en Suisse",
    hero_line1: "Circuits en Suisse",
    hero_line2: "rien que pour vous",
    hero_subtext:
      "La Suisse, en privé rien que pour vous — Alpes majestueuses, lacs cristallins et villages de conte avec des guides locaux expérimentés. 20 ans de voyages inoubliables, sur mesure pour vous.",
    hero_image_alt: "Panorama des Alpes suisses lors d'un circuit Voyage Switzerland",
    hero_btn_primary: "Réserver un circuit",
    hero_btn_secondary: "Explorer les circuits",
    tours_eyebrow: "Nos circuits",
    tours_title: "Circuits en Suisse",
    tours_subtitle:
      "Des itinéraires conçus avec soin à travers la Suisse — guidés, pittoresques et sans effort. Chaque circuit avec un expert local et une réservation flexible.",
    services_eyebrow: "Nos services",
    services_title: "Tout pour votre voyage en Suisse",
    services_subtitle:
      "Des aventures alpines privées aux arrivées sans stress à l'aéroport — une seule équipe pour tout votre voyage.",
    dest_eyebrow: "Destinations",
    dest_title: "Où irez-vous ?",
    dest_subtitle:
      "Six icônes de la Suisse — choisissez une destination, nous créons l'expérience parfaite.",
    why_eyebrow: "Le standard Voyage",
    why_title: "Pourquoi Voyage Switzerland ?",
    contact_eyebrow: "Contact",
    contact_title: "Planifions votre voyage",
    contact_subtitle:
      "Parlez-nous de vos rêves de voyage — nous répondons personnellement, généralement sous 24 heures.",
    footer_tagline:
      "Circuits privés guidés à travers la Suisse. Des destinations inoubliables avec des experts locaux — rapide, simple et fiable.",
    editorial_eyebrow: "Le Guide Voyage",
    editorial_title: "Des circuits en Suisse, conçus par des gens d'ici",
    editorial_p1:
      "La Suisse récompense les voyageurs qui prennent leur temps. Nos circuits privés en Suisse sont construits sur cette conviction : un guide-chauffeur local au volant, un itinéraire façonné selon vos envies et la liberté de s'attarder là où la lumière est la plus belle. Escapade d'une demi-journée dans un village lacustre ou journée complète à travers les cols alpins — chaque circuit inclut la prise en charge à votre porte, des horaires flexibles et de sincères conseils locaux qu'aucun guide ne mentionne.",
    editorial_p2:
      "De Zurich, Genève et Lucerne à Interlaken, Zermatt et Saint-Moritz, nos circuits de luxe couvrent les icônes du pays comme ses recoins tranquilles. Couples, familles et groupes jusqu'à 200 personnes voyagent avec nous pour des transferts aéroport, des journées de découverte et des voyages sur mesure — vingt ans d'hospitalité suisse, réservés en quelques minutes et confirmés personnellement par notre équipe.",
    editorial_p3:
      "Vous ne savez pas par où commencer ? Donnez-nous vos dates et vos envies, et nous dessinons la journée pour vous — sans engagement, sans frais cachés, avec une réponse sous 24 heures. C'est la promesse que nous tenons depuis notre tout premier circuit, il y a vingt ans.",
  },
  ar: {
    hero_badge: "جولات خاصة وفاخرة في سويسرا",
    hero_line1: "جولات في سويسرا",
    hero_line2: "صُممت من أجلك",
    hero_subtext:
      "سويسرا لك وحدك — جبال ألب مهيبة وبحيرات صافية وقرى من الحكايات برفقة مرشدين محليين خبراء. عشرون عامًا من صناعة رحلات لا تُنسى، مصممة بالكامل على مقاسك.",
    hero_image_alt: "بانوراما جبال الألب السويسرية خلال جولة مع Voyage Switzerland",
    hero_btn_primary: "احجز جولتك",
    hero_btn_secondary: "استكشف الجولات",
    tours_eyebrow: "جولاتنا",
    tours_title: "جولات في سويسرا",
    tours_subtitle:
      "مسارات مصممة بعناية عبر سويسرا — بمرافقة مرشدين، مناظر خلابة ودون عناء. كل جولة تشمل خبيرًا محليًا وحجزًا مرنًا.",
    services_eyebrow: "خدماتنا",
    services_title: "كل ما تحتاجه في سويسرا",
    services_subtitle:
      "من مغامرات الألب الخاصة إلى الوصول السلس من المطار — فريق واحد لرحلتك السويسرية بأكملها.",
    dest_eyebrow: "الوجهات",
    dest_title: "إلى أين ستذهب؟",
    dest_subtitle:
      "ست أيقونات من سويسرا — اختر وجهة وسنخطط التجربة المثالية.",
    why_eyebrow: "معيار Voyage",
    why_title: "لماذا Voyage Switzerland؟",
    contact_eyebrow: "تواصل معنا",
    contact_title: "لنخطط رحلتك معًا",
    contact_subtitle:
      "أخبرنا عن أحلام سفرك — نرد عليك شخصيًا، عادةً خلال ٢٤ ساعة.",
    footer_tagline:
      "جولات خاصة بمرافقة مرشدين عبر سويسرا. وجهات لا تُنسى مع خبراء محليين — بسرعة وسهولة وموثوقية.",
    editorial_eyebrow: "دليل Voyage",
    editorial_title: "جولات في سويسرا، يخططها من يعيشون هنا",
    editorial_p1:
      "سويسرا تكافئ المسافرين الذين يأخذون وقتهم. جولاتنا الخاصة في سويسرا مبنية على هذه القناعة: مرشد وسائق محلي خلف المقود، ومسار يُشكَّل حول اهتماماتك، وحرية التوقف حيث يكون الضوء أجمل. اختر جولة نصف يوم إلى قرية على البحيرة أو رحلة يوم كامل عبر ممرات الألب — كل جولة تشمل الاستقبال من الباب إلى الباب ومواعيد مرنة ونصائح محلية صادقة لن تجدها في أي دليل سياحي.",
    editorial_p2:
      "من زيورخ وجنيف ولوتسيرن إلى إنترلاكن وزيرمات وسان موريتز، تغطي جولاتنا الفاخرة أيقونات البلاد وأركانها الهادئة على حد سواء. يسافر معنا الأزواج والعائلات والمجموعات حتى 200 شخص — من التوصيل من المطار إلى أيام المشاهدة والباقات متعددة الأيام — عشرون عامًا من الضيافة السويسرية، تُحجز في دقائق ويؤكدها فريقنا شخصيًا.",
    editorial_p3:
      "لست متأكدًا من أين تبدأ؟ أخبرنا بتواريخك وأمنياتك وسنصمم لك اليوم المثالي — دون التزام ودون رسوم خفية وبرد خلال ٢٤ ساعة. هذا هو المعيار الذي حافظنا عليه منذ جولتنا الأولى قبل عقدين.",
  },
};

const HOME_ALTERNATES = { en: "/", de: "/de", fr: "/fr", ar: "/ar" };

export const HOME_SEO = {
  en: {
    lang: "en",
    title: "Voyage Swiss – Private Tours in Switzerland",
    description:
      "Discover Switzerland with Voyage Swiss. We offer luxury private tours, family tours, couple tours, group tours, and professional chauffeur services across Switzerland.",
    alternates: HOME_ALTERNATES,
  },
  de: {
    lang: "de-CH",
    title: "Touren in der Schweiz — Private & Luxus-Touren | Voyage Switzerland",
    description:
      "Private Touren und Luxusreisen in der Schweiz mit lokalen Fahrer-Guides — atemberaubende Alpenlandschaften, massgeschneiderte Reiserouten und persönliche Reisepakete.",
    alternates: HOME_ALTERNATES,
  },
  fr: {
    lang: "fr-CH",
    title: "Circuits en Suisse — Privés & de Luxe | Voyage Switzerland",
    description:
      "Circuits privés et de luxe en Suisse avec guides-chauffeurs locaux — paysages alpins à couper le souffle, itinéraires sur mesure et voyages personnalisés.",
    alternates: HOME_ALTERNATES,
  },
  ar: {
    lang: "ar",
    title: "جولات في سويسرا — جولات خاصة وفاخرة | Voyage Switzerland",
    description:
      "جولات خاصة وفاخرة في سويسرا مع مرشدين وسائقين محليين — مناظر جبال الألب الخلابة، مسارات مصممة خصيصًا وباقات سفر شخصية. احجز جولتك في سويسرا اليوم.",
    alternates: HOME_ALTERNATES,
  },
};
